export function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function isSecureCaptureContext(): boolean {
  return typeof window !== 'undefined' && window.isSecureContext;
}

export function supportsMediaRecorder(): boolean {
  return typeof window !== 'undefined' && typeof MediaRecorder !== 'undefined';
}

export function supportsUserMedia(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
}

export function supportsBrowserCapture(): boolean {
  return isSecureCaptureContext() && supportsMediaRecorder() && supportsUserMedia();
}

export interface MediaAttachmentMetadata {
  duration?: number;
  width?: number;
  height?: number;
}

export interface SmartThumbnailResult {
  blob: Blob | null;
  filename: string;
  frameTimeMs: number;
  selectionMode: 'auto' | 'manual';
}

const HAVE_CURRENT_FRAME_DATA = 2;

export async function readMediaMetadata(
  file: Blob,
  kind: 'audio' | 'video',
): Promise<MediaAttachmentMetadata> {
  if (typeof document === 'undefined') {
    return {};
  }

  const objectUrl = URL.createObjectURL(file);

  try {
    const element = kind === 'video'
      ? document.createElement('video')
      : document.createElement('audio');

    return await new Promise<MediaAttachmentMetadata>((resolve) => {
      const cleanup = () => {
        element.removeAttribute('src');
        element.load();
        URL.revokeObjectURL(objectUrl);
      };

      element.preload = 'metadata';
      if (kind === 'video') {
        (element as HTMLVideoElement).playsInline = true;
      }

      element.onloadedmetadata = () => {
        const result: MediaAttachmentMetadata = {
          duration: Number.isFinite(element.duration) ? element.duration : undefined,
        };

        if (kind === 'video') {
          const video = element as HTMLVideoElement;
          result.width = video.videoWidth || undefined;
          result.height = video.videoHeight || undefined;
        }

        cleanup();
        resolve(result);
      };

      element.onerror = () => {
        cleanup();
        resolve({});
      };

      element.src = objectUrl;
    });
  } catch {
    URL.revokeObjectURL(objectUrl);
    return {};
  }
}

function computeFrameCandidates(duration: number): number[] {
  if (!Number.isFinite(duration) || duration <= 1) {
    return [0];
  }

  return [0.2, 0.35, 0.5, 0.65, 0.8].map((fraction) => duration * fraction);
}

function scoreAverageBrightness(context: CanvasRenderingContext2D, width: number, height: number): number {
  const image = context.getImageData(0, 0, width, height);
  const { data } = image;
  if (data.length === 0) return 0;

  const IDEAL_BRIGHTNESS = 128;
  let total = 0;
  const pixelCount = data.length / 4;
  for (let index = 0; index < data.length; index += 4) {
    const r = data[index] ?? 0;
    const g = data[index + 1] ?? 0;
    const b = data[index + 2] ?? 0;
    total += (0.2126 * r) + (0.7152 * g) + (0.0722 * b);
  }

  const avgBrightness = total / pixelCount;
  return 1 - Math.abs(avgBrightness - IDEAL_BRIGHTNESS) / IDEAL_BRIGHTNESS;
}

export async function createSmartVideoThumbnail(
  file: Blob,
  filename = `video-thumb-${Date.now()}.jpg`,
): Promise<SmartThumbnailResult> {
  if (typeof document === 'undefined') {
    return {
      blob: null,
      filename,
      frameTimeMs: 0,
      selectionMode: 'auto',
    };
  }

  const objectUrl = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.preload = 'metadata';
  video.muted = true;
  video.playsInline = true;

  const cleanup = () => {
    video.pause();
    video.removeAttribute('src');
    video.load();
    URL.revokeObjectURL(objectUrl);
  };

  const waitForCurrentFrame = (): Promise<void> => new Promise((resolve, reject) => {
    if (video.readyState >= HAVE_CURRENT_FRAME_DATA) {
      resolve();
      return;
    }

    const onLoadedData = () => {
      video.removeEventListener('error', onError);
      resolve();
    };
    const onError = () => {
      video.removeEventListener('loadeddata', onLoadedData);
      reject(new Error('frame_load_failed'));
    };

    video.addEventListener('loadeddata', onLoadedData, { once: true });
    video.addEventListener('error', onError, { once: true });
  });

  const seekTo = (time: number): Promise<void> => new Promise((resolve, reject) => {
    if (time <= 0 && video.currentTime === 0) {
      waitForCurrentFrame().then(resolve, reject);
      return;
    }

    const onSeeked = () => {
      video.removeEventListener('error', onError);
      resolve();
    };
    const onError = () => {
      video.removeEventListener('seeked', onSeeked);
      reject(new Error('seek_failed'));
    };

    video.addEventListener('seeked', onSeeked, { once: true });
    video.addEventListener('error', onError, { once: true });
    video.currentTime = Math.max(0, time);
  });

  try {
    await new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => resolve();
      video.onerror = () => reject(new Error('video_load_failed'));
      video.src = objectUrl;
    });

    const duration = Number.isFinite(video.duration) ? video.duration : 0;
    const MAX_THUMBNAIL_DIM = 1280;
    let width = video.videoWidth || 0;
    let height = video.videoHeight || 0;

    if (width > MAX_THUMBNAIL_DIM || height > MAX_THUMBNAIL_DIM) {
      const scale = MAX_THUMBNAIL_DIM / Math.max(width, height);
      width = Math.round(width * scale);
      height = Math.round(height * scale);
    }

    if (!width || !height) {
      cleanup();
      return { blob: null, filename, frameTimeMs: 0, selectionMode: 'auto' };
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) {
      cleanup();
      return { blob: null, filename, frameTimeMs: 0, selectionMode: 'auto' };
    }

    let bestScore = Number.NEGATIVE_INFINITY;
    let bestBlob: Blob | null = null;
    let bestTime = 0;

    for (const candidateTime of computeFrameCandidates(duration)) {
      await seekTo(candidateTime);
      context.drawImage(video, 0, 0, width, height);
      const score = scoreAverageBrightness(context, width, height);
      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((result) => resolve(result), 'image/jpeg', 0.82);
      });

      if (blob && score > bestScore) {
        bestScore = score;
        bestBlob = blob;
        bestTime = Math.round(candidateTime * 1000);
      }
    }

    cleanup();

    return {
      blob: bestBlob,
      filename,
      frameTimeMs: bestBlob ? bestTime : 0,
      selectionMode: 'auto',
    };
  } catch {
    cleanup();
    return {
      blob: null,
      filename,
      frameTimeMs: 0,
      selectionMode: 'auto',
    };
  }
}

export function normalizeWaveform(samples: number[], targetCount = 48): number[] {
  if (samples.length === 0) {
    return [];
  }

  const bucketSize = Math.max(1, Math.floor(samples.length / targetCount));
  const buckets: number[] = [];

  for (let index = 0; index < samples.length; index += bucketSize) {
    const slice = samples.slice(index, index + bucketSize);
    const average = slice.reduce((sum, value) => sum + value, 0) / slice.length;
    buckets.push(average);
  }

  const max = Math.max(...buckets, 1);
  return buckets.slice(0, targetCount).map((value) => value / max);
}
